import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Download, Loader2, Pencil, RefreshCw, Send, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PAYSLIP_STATUS_BADGE_VARIANT, formatPeriodLabel } from "@/components/payroll/format";
import {
  useDiscardPayrollPayslip,
  usePayrollPayslip,
  usePayrollPayslipArtifacts,
  useRefreshPayrollPayslipStatus,
  useTransmitPayrollPayslip,
} from "@/lib/api/payroll-payslips.queries";
import { PayslipForm } from "@/components/payroll/PayslipForm";
import { translatePayrollError } from "@/components/payroll/error-translations";
import { DiscardDraftDialog } from "@/components/shared/DiscardDraftDialog";
import { useTeamPermission } from "@/lib/team-context";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import {
  useCompanyDianReadiness,
  useDianReadinessReason,
} from "@/lib/dian/use-company-dian-readiness";
import { useActiveCompany } from "@/lib/company-context";
import { showEmitErrorToast } from "@/lib/dian/emit-error-toast";
import { DocumentJournalPanel } from "@/components/journal/DocumentJournalPanel";
import { getPayrollPayslipArtifact } from "@/lib/api/payroll-payslips";
import { downloadBlob } from "@/lib/download-blob";
import { safeArtifactFileName } from "@/components/sales-invoices/artifact-download";

export const Route = createFileRoute(
  "/companies/$companyId/nomina/payslips/$payslipId",
)({
  component: PayslipDetailPage,
});

function PayslipDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <PayslipDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function PayslipDetailContent() {
  const formatCop = useMoneyFormatter();
  const [downloadingArtifact, setDownloadingArtifact] = useState<string | null>(null);
  const { t } = useTranslation();
  const { companyId, payslipId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const payslipQ = usePayrollPayslip(companyId, payslipId);
  const transmit = useTransmitPayrollPayslip(companyId);
  const refresh = useRefreshPayrollPayslipStatus(companyId);
  const discard = useDiscardPayrollPayslip(companyId);
  const canWrite = useTeamPermission("payroll.write");
  const [editing, setEditing] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const readiness = useCompanyDianReadiness(companyId, "nomina");
  const readinessReason = useDianReadinessReason(readiness, t);
  const artifactsQ = usePayrollPayslipArtifacts(
    companyId,
    payslipId,
    ["transmitted", "failed", "replaced", "voided"].includes(payslipQ.data?.status ?? ""),
  );

  if (payslipQ.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (payslipQ.isError || !payslipQ.data) {
    return (
      <ErrorState
        title={t("payroll.payslip.detail.error_title")}
        description={t("payroll.payslip.detail.error_body")}
        onRetry={() => void payslipQ.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const p = payslipQ.data;
  const canTransmit = p.status === "draft" || p.status === "failed";
  // A draft, or a payslip DIAN confirmed as rejected, can be reopened and corrected (PF-463).
  const canEdit = canWrite && p.editable;
  const canDiscard = canWrite && p.status === "draft" && p.documentNumber === 0 && !p.boundLiquidationId;

  async function onTransmit() {
    try {
      await transmit.mutateAsync({ payslipId });
      toast.success(t("payroll.toast.payslip_queued", { documentId: p.documentId }));
    } catch (err) {
      showEmitErrorToast(err, translatePayrollError(err, t), t, () =>
        void navigate({
          to: "/companies/$companyId/resoluciones",
          params: { companyId },
        }),
      );
    }
  }

  async function onRefresh() {
    try {
      await refresh.mutateAsync({ payslipId });
      toast.success(t("payroll.toast.refresh_done"));
    } catch (err) {
      toast.error(translatePayrollError(err, t));
    }
  }

  async function onDiscard() {
    try {
      await discard.mutateAsync({ payslipId, expectedRevision: p.revision });
      toast.success(t("payroll.toast.payslip_discarded"));
      setDiscardOpen(false);
      void navigate({ to: "/companies/$companyId/nomina/payslips", params: { companyId } });
    } catch (err) {
      toast.error(translatePayrollError(err, t));
      setDiscardOpen(false);
    }
  }

  async function onDownloadArtifact(name: string) {
    setDownloadingArtifact(name);
    try {
      const blob = await getPayrollPayslipArtifact(companyId, payslipId, name);
      downloadBlob(blob, safeArtifactFileName(p.documentId, name, blob.type));
    } catch {
      toast.error(t("payroll.artifacts.download_error"));
    } finally {
      setDownloadingArtifact(null);
    }
  }

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <DianReadinessBanner companyId={companyId} documentKind="nomina" />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/nomina/payslips"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("payroll.payslip.detail.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight font-mono">
            {p.documentId}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {p.employeeFullName ?? "-"} • {formatPeriodLabel(p.periodStart, p.periodEnd)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {["transmitted", "replaced", "voided"].includes(p.status) && ["00", "02"].includes(p.dianStatus ?? "") && p.processId ? (
            <Button variant="outline" disabled={downloadingArtifact !== null} onClick={() => void onDownloadArtifact("pdf")}>
              {downloadingArtifact === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {t("payroll.actions.download_pdf")}
            </Button>
          ) : null}
          <Badge variant={PAYSLIP_STATUS_BADGE_VARIANT[p.status]} className="text-[10px] uppercase">
            {t(`payroll.payslip.status.${p.status}`)}
          </Badge>
          {canEdit && !editing ? (
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
              {t("payroll.actions.edit")}
            </Button>
          ) : null}
          {canDiscard && !editing ? (
            <Button variant="outline" onClick={() => setDiscardOpen(true)} disabled={discard.isPending}>
              <Trash2 className="h-4 w-4" />
              {t("payroll.actions.discard")}
            </Button>
          ) : null}
          {canTransmit && !editing ? (
            <Button
              onClick={onTransmit}
              disabled={transmit.isPending || !readiness.ready}
              title={!readiness.ready ? readinessReason : undefined}
            >
              {transmit.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {t("payroll.actions.emit_now")}
            </Button>
          ) : canTransmit ? null : (
            <Button onClick={onRefresh} variant="outline" disabled={refresh.isPending}>
              {refresh.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              {t("payroll.actions.refresh_status")}
            </Button>
          )}
        </div>
      </div>

      {editing ? (
        <PayslipForm
          companyId={companyId}
          initialPayslip={p}
          onSaved={() => setEditing(false)}
          onCancel={() => setEditing(false)}
        />
      ) : (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("payroll.payslip.totals.title")}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 text-sm">
          {(p.salariedTravelAllowance ?? 0) > 0 && <Row label={t("payrollTravel.salaried")} value={formatCop(p.salariedTravelAllowance)} />}
          {(p.nonSalariedTravelAllowance ?? 0) > 0 && <Row label={t("payrollTravel.nonSalaried")} value={formatCop(p.nonSalariedTravelAllowance)} />}
          <Row label={t("payroll.payslip.totals.totalEarnings")} value={formatCop(p.totalEarnings)} />
          <Row label={t("payroll.payslip.totals.totalDeductions")} value={formatCop(p.totalDeductions)} />
          <Row label={t("payroll.payslip.totals.totalPayable")} value={formatCop(p.totalPayable)} bold />
        </CardContent>
      </Card>
      )}

      {p.cune ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("payroll.cune.title")}</CardTitle>
            <CardDescription className="text-xs">
              {t("payroll.cune.subtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <code className="break-all text-xs font-mono">{p.cune}</code>
          </CardContent>
        </Card>
      ) : null}

      {artifactsQ.data && artifactsQ.data.artifacts.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("payroll.artifacts.title")}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {artifactsQ.data.artifacts.map((a) => (
              <button
                key={a.name}
                type="button"
                disabled={downloadingArtifact !== null}
                onClick={() => void onDownloadArtifact(a.name)}
                className="flex items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-accent"
              >
                <span>{t(`payroll.artifacts.name.${a.name}`, a.name)}</span>
                <span className="text-xs text-muted-foreground">{a.contentType ?? ""}</span>
              </button>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("journal.document.title")}</CardTitle>
          <CardDescription>{t("journal.document.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <DocumentJournalPanel
            companyId={companyId}
            sourceTable="payroll_payslips"
            documentId={payslipId}
          />
        </CardContent>
      </Card>

      <DiscardDraftDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={t("payroll.payslip.detail.discard_title", { documentId: p.documentId })}
        description={t("payroll.payslip.detail.discard_body")}
        pending={discard.isPending}
        onConfirm={() => void onDiscard()}
      />
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex items-center justify-between ${bold ? "font-semibold" : "text-muted-foreground"}`}>
      <span>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
